// eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/ban-ts-ignore
// @ts-ignore
import React, { FC, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import _, { toNumber } from 'lodash';

import * as MoffAPIActions from '../actions/MoffAPI';

import * as UserListField from '../constants/UserListField';
import * as UserBasicInfoListField from '../constants/MoffRaku';
import { COMPLICATION_FIELDS, ComplicationKey } from '../constants/MoffRaku';
import { MENU_TYPE } from '../constants/Menu';

// eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/ban-ts-ignore
// @ts-ignore
import jspreadsheet from 'jspreadsheet-ce';
import '../../node_modules/jspreadsheet-ce/dist/jspreadsheet.css';
import '../../node_modules/jsuites/dist/jsuites.css';
import { useDispatch, useSelector } from 'react-redux';
import store from '../store/configureStore';
import GeneralPrimaryGhostButton from '../components/Atoms/GeneralPrimaryGhostButton';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { green } from '@material-ui/core/colors';

// 自動保存トグル用の緑色 + 大きめ Switch (#834 C: テーマ依存のオレンジを上書き)
const GreenSwitch = withStyles({
  root: {
    width: 56,
    height: 32,
    padding: 6,
  },
  switchBase: {
    padding: 6,
    color: '#bbb',
    '&$checked': {
      color: green[600],
      transform: 'translateX(24px)',
    },
    '&$checked + $track': {
      backgroundColor: green[600],
      opacity: 1,
    },
  },
  thumb: {
    width: 20,
    height: 20,
  },
  track: {
    height: 20,
    borderRadius: 10,
    backgroundColor: '#bbb',
    opacity: 1,
  },
  checked: {},
})(Switch);

const useStyles = makeStyles(() => ({
  spreadsheet: {
    // 編集禁止カラム (利用者基本情報: 氏名〜保険者番号) を視覚的に区別する。
    // jspreadsheet-ce は readOnly:true の td に class="readonly" を付与する。
    // jspreadsheet 側に `.jexcel > tbody > tr > td.readonly { color: rgba(0,0,0,0.3) }`
    // という高 specificity の既定があり、単純な `td.readonly` セレクタでは color が
    // 上書きされない。同形のセレクタで color を指定して打ち勝たせる。
    '& .jexcel > tbody > tr > td.readonly': {
      color: '#666',
    },
    '& td.readonly': {
      backgroundColor: '#f0f0f0',
      cursor: 'not-allowed',
    },
    // 未保存の編集セル (#834 F) を視覚的に強調。保存成功時にクラスを除去する。
    '& td.dirty': {
      backgroundColor: '#fff8c5',
      fontWeight: 'bold',
    },
    // (#834 スクロール可視化 C) 凍結列右端に明確な影を付与し
    // 「右にもっと項目がある」ことを視覚的に示唆する。
    // 既定 (jspreadsheet) は 0.2px #ccc と非常に subtle で気づかれない。
    // tbody (一覧行) にも適用 (既定では thead/tfoot のみ影付き)。
    '& .jexcel tbody td.jexcel_freezed, & .jexcel thead .jexcel_freezed': {
      boxShadow: '4px 0 6px -2px rgba(0, 0, 0, 0.2) !important',
    },
    // (#850) ヘッダー行の項目名が固定幅 (例 width:70 の合併症列「うっ血性心不全」)
    // で見切れる問題を解消する。jspreadsheet-ce 既定は
    //   .jexcel { white-space: nowrap } + .jexcel thead td { overflow: hidden }
    // のため、列幅より長い見出しは ellipsis すら出ずに途中で切れる。
    // ・white-space: pre-line で title 内の `\n` (例 '生年月日\n(和暦)') を改行扱いに
    // ・word-break: break-word で改行指定がなくても列幅で折り返す
    // ・vertical-align: middle + line-height で 1〜2 行どちらでも体裁を保つ
    // 絞り込み検索行 (tr.jexcel_filter) も thead 内にあるが、こちらは横並びの
    // ツールバーで折り返してほしくないため :not(.jexcel_filter) で除外する。
    '& .jexcel > thead > tr:not(.jexcel_filter) > td': {
      whiteSpace: 'pre-line',
      wordBreak: 'break-word',
      verticalAlign: 'middle',
      lineHeight: 1.3,
    },
  },
  // (#834 スクロール可視化 A) スクロールバーを常時表示・少し太めに
  // 既定 8px は macOS の overlay scrollbar で気づかれにくい。
  // ただし macOS は overlay scrollbar の最大サイズが OS 側で制限されているため、
  // 14-16px 以上を指定しても見た目はそれ以上太くならない。
  // 大幅な太さの強調はスクロールボタン (下記 ◀▶) で代替する。
  '@global': {
    '.jexcel_content::-webkit-scrollbar': {
      width: '14px !important',
      height: '14px !important',
    },
    '.jexcel_content::-webkit-scrollbar-track': {
      background: '#e8e8e8 !important',
    },
    '.jexcel_content::-webkit-scrollbar-thumb': {
      background: '#999 !important',
      borderRadius: '7px !important',
    },
    '.jexcel_content::-webkit-scrollbar-thumb:hover': {
      background: '#666 !important',
    },
    '.jexcel_content': {
      scrollbarWidth: 'auto !important',
      scrollbarColor: '#999 #e8e8e8 !important',
    },
  },
}));

interface UserSelectBodyProps {
  users: UserListField.User[];
  type: MENU_TYPE;
  icd10: UserBasicInfoListField.ICD10[];
  disease: UserBasicInfoListField.Disease[];
}

type AllState = ReturnType<typeof store.getState>;

// 利用者一覧の表のフィールド表示用コンポーネント
const UserInputMoffRakuContainer: FC<UserSelectBodyProps> = ({ users, type, disease }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  // jspreadsheet
  const jRef = useRef(null);
  // 未保存の編集セル (#834 F): key は "y,x" 形式。保存成功時に clear する。
  // useRef で持つことで cell change の度に再レンダリングを発生させない。
  const dirtyCells = useRef<Set<string>>(new Set());
  // 初回データロード完了フラグ (#834 C 修正)。
  // PUT 成功時に Redux SUCCESS reducer が result を置き換えるため
  // result.users が空になり、後続の redirect useEffect が
  // window.location.href で勝手にリロードしてしまう。
  // 一度でもデータを読み込めていれば redirect をスキップしてバックグラウンド保存を維持する。
  const hasLoadedDataRef = useRef<boolean>(false);

  // 自動保存設定 (#834 C): 既定 OFF。localStorage に永続化してリロード後も維持。
  const AUTOSAVE_KEY = 'moffRakuUsers.autosaveEnabled';
  const AUTOSAVE_DEBOUNCE_MS = 2000;
  const initialAutosave = (() => {
    try {
      return window.localStorage.getItem(AUTOSAVE_KEY) === 'true';
    } catch {
      return false;
    }
  })();
  const [autosaveEnabled, setAutosaveEnabled] = useState<boolean>(initialAutosave);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // onchange (useEffect 内クロージャ) から最新値を参照するため ref に同期
  const autosaveEnabledRef = useRef<boolean>(initialAutosave);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 自動保存トグルを jspreadsheet 生成の絞り込み検索行 (.jexcel_filter) 左側に
  // React Portal で注入する (#834 C: トグルを表上部の左に配置)
  const [filterToolbarEl, setFilterToolbarEl] = useState<HTMLElement | null>(null);
  // 絞り込み検索ボックス側 (.jexcel_filter 右側) にヘルプアイコンを Portal で注入する
  const [searchToolbarEl, setSearchToolbarEl] = useState<HTMLElement | null>(null);

  const moffAPIGlobalState = useSelector((state: AllState) => state.moffAPI);
  const [cachedUsers, setCachedUsers] = useState<UserListField.User[]>([]);
  const [usersBasicInfo, setUsersBasicInfo] = useState<UserBasicInfoListField.BasicInfoFormat[]>([]);
  const [options, setOptions] = useState({});
  const [users_table_data, setUsersTableData] = useState({});

  const INDEX_OF_BOUNDARY_FOR_EDITABLE = 17;
  //ICD10トリガー
  const INDEX_OF_ICD10_TRIGGER_1 = 26;

  const to_string_col_index = (numeric_col_index: number) => {
    const RADIX = 26;
    const A = 'A'.charCodeAt(0);
    let n = numeric_col_index;
    let s = '';
    while (n >= 1) {
      n--;
      s = String.fromCharCode(A + (n % RADIX)) + s;
      n = Math.floor(n / RADIX);
    }
    return s;
  };

  // const setICD10 = (value: any, cell: string) => {
  //   // valueが icd10のnameに該当するものがあればその配列番号のicd10がICD10コードなので、それをセルに挿入
  //   const code = icd10.find(item => {
  //     return item.name === value.toString()
  //   });
  //   if (code){
  //     setValue(cell, code.icd10);
  //   }
  // }
  // const setValue = (cell: string, value: string) => {
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  //   // @ts-ignore
  //   jRef.current.jspreadsheet.setValue(cell, value);
  // }

  /** 和暦変換 **/
  const era_year = (year: string, month: string): [string, string] => {
    try {
      const date = new Date(Number(year), Number(month) - 1);
      const date_format_option = { era: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const formatter = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', date_format_option).formatToParts(date);
      const disp_era = formatter[0].value;
      const disp_year = formatter[1].value;
      return [disp_era, disp_year];
    } catch (e) {
      // console.log(e.toString());
      return ['', ''];
    }
  };

  /** 和暦から西暦に変換 **/
  const era_to_year = (name: string, year: number): number => {
    if (!name || !year) return 0;
    if (isNaN(year)) return 0;
    if (year <= 0) return 0;
    const data = [
      {
        name: '令和',
        year: 31,
        startYear: 2019,
      },
      {
        name: '平成',
        year: false,
        startYear: 1988,
      },
      {
        name: '昭和',
        year: 64,
        startYear: 1925,
      },
      {
        name: '大正',
        year: 15,
        startYear: 1911,
      },
      {
        name: '明治',
        year: 45,
        startYear: 1867,
      },
    ];
    for (let i = 0, len = data.length; i < len; i++) {
      if (name === data[i].name && (data[i].year === false || year <= data[i].year)) {
        const startYear = toNumber(data[i].startYear);
        return startYear + year;
      }
    }
    return 0;
  };

  // 内部保存処理: API 送信 + dirty クリア。確認ダイアログは含めない。
  // 手動保存 (saveAllData) と自動保存 (#834 C) の両方から呼び出される。
  const performSave = async (data: any) => {
    const data_array = [...data];
    const data_json_array = data_array.map((row) => {
      return {
        user_id: row[0],
        user_name: row[1],
        user_name_kana: row[2],
        user_gender: row[3],
        user_birth_year: era_to_year(row[4], toNumber(row[5])),
        user_birth_month: row[6],
        user_birth_day: row[7],
        user_care_level: row[8],
        user_care_id: row[9],
        user_visit_day: [row[10], row[11], row[12], row[13], row[14], row[15], row[16]],
        user_insurer_number: row[17],
        planner: row[18],
        planner_job: row[19],
        program_planner: row[20],
        program_planner_job: row[21],
        independence_level_disabled_elderly: row[22],
        independence_level_dementia_elderly: row[23],
        user_wish: row[24],
        family_wish: row[25],
        disease_name: row[26],
        idc10_disease_name: row[27],
        illness_era: row[28],
        illness_year: row[29],
        illness_month: row[30],
        illness_day: row[31],
        hospitalization_era: row[32],
        hospitalization_year: row[33],
        hospitalization_month: row[34],
        hospitalization_day: row[35],
        discharge_era: row[36],
        discharge_year: row[37],
        discharge_month: row[38],
        discharge_day: row[39],
        course_of_treatment: row[40],
        ...COMPLICATION_FIELDS.reduce((acc, { key }, idx) => {
          // checkbox 列のセル値は jspreadsheet が boolean (true/false) で返す。
          // 防御として `=== true` 比較で truthy 文字列等が紛れ込んでも false に倒す。
          acc[key] = row[41 + idx] === true;
          return acc;
        }, {} as Record<ComplicationKey, boolean>),
      };
    });
    const request_data = { data: data_json_array };
    await dispatch(MoffAPIActions.putMoffRakuUsers(request_data));
    // 保存成功後に dirty マークをクリア (例外時は throw されてここに到達しない想定)
    dirtyCells.current.clear();
    document.querySelectorAll('table.jexcel td.dirty').forEach((el) => el.classList.remove('dirty'));
  };

  // 手動保存: 確認ダイアログを表示してから performSave を呼ぶ
  const saveAllData = async (data: any) => {
    // #834 F: dirty 件数を確認ダイアログに表示
    const dirtyCount = dirtyCells.current.size;
    const confirmMessage =
      dirtyCount === 0
        ? '変更されたセルはありませんが、現在の内容で保存しますか？'
        : `${dirtyCount} 件の変更を保存します。\nよろしいですか？`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    await performSave(data);
  };

  const getAllData = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const data = jRef.current.jspreadsheet.getData();
    // Object.keys(data).map((key, index) => {
    //   console.log(key, index, data[key]);
    // });
    saveAllData(data);
  };

  // const download = () => {
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  //   // @ts-ignore
  //   jRef.current.jspreadsheet.download();
  // };

  // 横スクロールハンドラ (#834): macOS overlay scrollbar が薄くて気づかれにくいため、
  // 明示的なボタンで .jexcel_content の scrollLeft を動かす。
  // delta 正で右へ、負で左へ。スムーズスクロールで操作対象が分かりやすいよう smooth 指定。
  const SCROLL_STEP_PX = 400;
  const scrollTableHorizontally = (delta: number) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const content = (jRef.current as HTMLElement)?.querySelector?.('.jexcel_content') as HTMLElement | null;
    if (content) {
      content.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  // 自動保存トリガー (#834 C): debounce 後の実保存処理。
  // 自動保存中の状態を画面に表示しつつ、失敗時も dirty を残して再試行可能にする。
  const triggerAutosave = async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    if (!jRef.current?.jspreadsheet) return;
    if (dirtyCells.current.size === 0) return;
    setAutosaveStatus('saving');
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      const data = jRef.current.jspreadsheet.getData();
      await performSave(data);
      setAutosaveStatus('saved');
      // 数秒後に "saved" 表示を消す
      window.setTimeout(() => setAutosaveStatus('idle'), 3000);
    } catch (err) {
      // 自動保存失敗: dirty マークは残るので次回の編集 or 手動保存で再試行できる
      // eslint-disable-next-line no-console
      console.error('autosave failed:', err);
      setAutosaveStatus('error');
    }
  };

  // 自動保存設定の変更を ref と localStorage に同期
  useEffect(() => {
    autosaveEnabledRef.current = autosaveEnabled;
    try {
      window.localStorage.setItem(AUTOSAVE_KEY, String(autosaveEnabled));
    } catch {
      // localStorage 不可環境 (private mode 等) は無視: メモリ上の state のみ有効
    }
    // OFF に切り替えたら debounce 中のタイマーをキャンセル
    if (!autosaveEnabled && autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, [autosaveEnabled]);

  // 未保存変更がある状態でのページ離脱を防ぐ (#834 C: beforeunload)
  // 自動保存 ON でも debounce 待ち or 失敗中の取りこぼしを防ぐため常時有効。
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyCells.current.size > 0) {
        e.preventDefault();
        // 多くのブラウザは独自文言を表示するためメッセージは無視されるが互換のため設定
        e.returnValue = '保存していない変更があります。ページを離れてもよろしいですか？';
        return e.returnValue;
      }
      return undefined;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // unmount 時の autosave タイマー clean up (発火後の no-op を防ぐ)
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!_.isEmpty(moffAPIGlobalState.result.users)) {
      //const sortedUsers = duplicateRepeatSort(USERS_SORT_KEYS, moffAPIGlobalState.result, objectQuickSort);
      setCachedUsers(moffAPIGlobalState.result.users);
      hasLoadedDataRef.current = true;
    }
    if (!_.isEmpty(moffAPIGlobalState.result.usersBasicInfo)) {
      //const sortedUsers = duplicateRepeatSort(USERS_SORT_KEYS, moffAPIGlobalState.result, objectQuickSort);
      setUsersBasicInfo(moffAPIGlobalState.result.usersBasicInfo);
      hasLoadedDataRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moffAPIGlobalState.Loaded, moffAPIGlobalState.result]);

  useEffect(() => {
    // 初回データロード後の redirect をスキップ (#834 C 修正)
    // PUT 保存後に Redux result が空になっても、すでに表示中の表は維持したい。
    if (hasLoadedDataRef.current) return;
    // setUrlBase(type.urlBase);
    if (
      _.isEmpty(moffAPIGlobalState.result.users) &&
      _.isEmpty(moffAPIGlobalState.result.usersBasicInfo) &&
      _.isEmpty(users) &&
      type === MENU_TYPE.MoffRakuUsers
    ) {
      window.location.href = '/moff_raku_users';
    } else if (_.isEmpty(users) && type === MENU_TYPE.MoffRakuUsers) {
      alert('利用者を登録してください');
      window.location.href = '/admin.html';
    }
  }, [moffAPIGlobalState.result, type, users]);

  useLayoutEffect(() => {
    //TODO
    // userのオブジェクトの入ったhiddenのClass.
    const usersObject: HTMLCollectionOf<Element> = document.getElementsByClassName('user');
    let index = 0;
    for (const el in usersObject) {
      if (Object.prototype.hasOwnProperty.call(usersObject, el)) {
        // 1つのエレメント.
        const parent: HTMLElement | null = usersObject[el].parentElement;

        if (parent) {
          if (parent.hidden === false) {
            const isEven: boolean = index % 2 === 0;
            [].forEach.call(
              parent.childNodes,
              (child: Node) => ((child as HTMLElement).style.background = isEven ? '#fff' : '#e8e4df'),
            );
            index++;
          }
        }
      }
    }
  }, [users]);

  useEffect(() => {
    // const users_data = !cachedUsers ? [] : cachedUsers.map((user) => {
    setUsersTableData(
      !cachedUsers
        ? {}
        : cachedUsers.map((user) => {
            const user_id = user.user_id;
            const birth = era_year(user.user_birth_year, user.user_birth_month);
            const gender = user.user_gender === 0 ? '男' : '女';
            // user_care_levelの数字 要支援1,要支援2,要介護1,要介護2,要介護3,要介護4,要介護5の順に0,1,2,3,4,5,6の整数が対応する
            const care_level =
              Number(user.user_care_level) === 0
                ? '要支援1'
                : Number(user.user_care_level) === 1
                ? '要支援2'
                : Number(user.user_care_level) === 2
                ? '要介護1'
                : Number(user.user_care_level) === 3
                ? '要介護2'
                : Number(user.user_care_level) === 4
                ? '要介護3'
                : Number(user.user_care_level) === 5
                ? '要介護4'
                : Number(user.user_care_level) === 6
                ? '要介護5'
                : Number(user.user_care_level) === 906
                ? '事業対象者'
                : Number(user.user_care_level) === 999
                ? '要介護なし'
                : '';
            // user_visit_day [月,火,水,木,金,土,日]の順にサイズ7の配列が入る。
            const visit_day = user.user_visit_day ? user.user_visit_day : [0, 0, 0, 0, 0, 0, 0];

            // usersBasicinfoでuser_idが同一のものを結合
            const user_basicinfo = !usersBasicInfo
              ? undefined
              : usersBasicInfo.find((item) => item.user_id === user.user_id);
            if (user_basicinfo === undefined || user_basicinfo === null) {
              return [
                user_id,
                user.user_name,
                user.user_name_kana,
                gender,
                birth[0],
                birth[1],
                user.user_birth_month,
                user.user_birth_day,
                care_level,
                user.user_care_id,
                visit_day[0],
                visit_day[1],
                visit_day[2],
                visit_day[3],
                visit_day[4],
                visit_day[5],
                visit_day[6],
                user.user_insurer_no,
                // 合併症 23 列の初期値: 利用者基本情報未登録の利用者は全て false (未チェック)
                ...Array(23).fill(false),
              ];
            }
            const planner = user_basicinfo.planner;
            const planner_job = user_basicinfo.planner_job;
            const program_planner = user_basicinfo.program_planner;
            const program_planner_job = user_basicinfo.program_planner_job;
            const independence_level_disabled_elderly = user_basicinfo.independence_level_disabled_elderly;
            const independence_level_dementia_elderly = user_basicinfo.independence_level_dementia_elderly;
            const user_wish = user_basicinfo.user_wish;
            const family_wish = user_basicinfo.family_wish;
            const disease_name = user_basicinfo.disease_name;
            const idc10_disease_name = user_basicinfo.idc10_disease_name;
            const illness_era = user_basicinfo.illness_era;
            const illness_year = user_basicinfo.illness_year;
            const illness_month = user_basicinfo.illness_month;
            const illness_day = user_basicinfo.illness_day;
            const hospitalization_era = user_basicinfo.hospitalization_era;
            const hospitalization_year = user_basicinfo.hospitalization_year;
            const hospitalization_month = user_basicinfo.hospitalization_month;
            const hospitalization_day = user_basicinfo.hospitalization_day;
            const discharge_era = user_basicinfo.discharge_era;
            const discharge_year = user_basicinfo.discharge_year;
            const discharge_month = user_basicinfo.discharge_month;
            const discharge_day = user_basicinfo.discharge_day;
            const course_of_treatment = user_basicinfo.course_of_treatment;
            // 合併症は API 上 boolean | undefined。checkbox 列にはそのまま boolean を渡す
            // (`=== true` で undefined/null を false に正規化)。
            const complicationCells = COMPLICATION_FIELDS.map(({ key }) => user_basicinfo[key] === true);

            return [
              user_id,
              user.user_name,
              user.user_name_kana,
              gender,
              birth[0],
              birth[1],
              user.user_birth_month,
              user.user_birth_day,
              care_level,
              user.user_care_id,
              visit_day[0],
              visit_day[1],
              visit_day[2],
              visit_day[3],
              visit_day[4],
              visit_day[5],
              visit_day[6],
              user.user_insurer_no,
              planner,
              planner_job,
              program_planner,
              program_planner_job,
              independence_level_disabled_elderly,
              independence_level_dementia_elderly,
              user_wish,
              family_wish,
              disease_name,
              idc10_disease_name,
              illness_era,
              illness_year,
              illness_month,
              illness_day,
              hospitalization_era,
              hospitalization_year,
              hospitalization_month,
              hospitalization_day,
              discharge_era,
              discharge_year,
              discharge_month,
              discharge_day,
              course_of_treatment,
              ...complicationCells,
            ];
          }),
    );
  }, [cachedUsers, usersBasicInfo]);

  useEffect(() => {
    // #857: jSuites の dropdown autocomplete (find) は label.match(regex) で絞り込むため、
    // source の item が string 以外 (undefined / number など) だと TypeError でループが中断し
    // 「候補リストが空」状態になる。次の 2 点で防ぐ:
    //   1. usersBasicInfo.disease_name は undefined を取り得るので filter で除外
    //   2. disease.icd10 は API レスポンス上 number で来る (例: 5920004) ので String() に変換
    const disease_names = Array.from(
      new Set<string>(
        ['', ...usersBasicInfo.map((item) => item.disease_name), ...disease.map((item) => item.name)].filter(
          (n): n is string => typeof n === 'string',
        ),
      ),
    );
    const disease_name = !disease ? [] : disease_names;
    const disease_code = !disease ? [] : disease.map((item) => String(item.icd10));
    setOptions({
      data: users_table_data,
      //minDimensions: [10, 10],
      tableOverflow: true,
      tableHeight: '1000px',
      tableWidth: '1000px',
      // 横スクロール時に氏名・ふりがなが見えなくなるのを防ぐため左端固定。
      // 内訳: hidden user_id (col 0) + 氏名 (col 1) + ふりがな (col 2) の 3 列。
      freezeColumns: 3,
      // 表上部に検索ボックスを表示。全カラムを対象に部分一致で絞り込む。
      search: true,
      // jspreadsheet-ce 既定の "Search:" を日本語ラベルに差し替え。
      text: {
        search: '絞り込み検索',
      },
      columns: [
        { type: 'hidden', title: 'user_id' },
        { type: 'text', title: '氏名', width: 100, readOnly: true },
        { type: 'text', title: 'ふりがな', width: 100, readOnly: true },
        { type: 'dropdown', title: '性別', width: 80, source: ['男', '女'], readOnly: true },
        {
          type: 'dropdown',
          title: '生年月日\n(和暦)',
          width: 80,
          source: ['大正', '昭和', '平成', '令和'],
          readOnly: true,
        },
        { type: 'numeric', title: '生年月日\n(年)', width: 80, mask: '00', readOnly: true },
        { type: 'numeric', title: '生年月日\n(月)', width: 80, mask: '00', readOnly: true },
        { type: 'numeric', title: '生年月日\n(日)', width: 80, mask: '00', readOnly: true },
        {
          type: 'dropdown',
          title: '要介護度',
          width: 100,
          source: [
            '要支援1',
            '要支援2',
            '要介護1',
            '要介護2',
            '要介護3',
            '要介護4',
            '要介護5',
            '非該当',
            '事業対象者',
            '要支援(経過的要介護)',
          ],
          readOnly: true,
        },
        { type: 'text', title: '被保険者番号', width: 100, readOnly: true },
        { type: 'checkbox', title: '通所\n(月)', width: 40, readOnly: true },
        { type: 'checkbox', title: '通所\n(火)', width: 40, readOnly: true },
        { type: 'checkbox', title: '通所\n(水)', width: 40, readOnly: true },
        { type: 'checkbox', title: '通所\n(木)', width: 40, readOnly: true },
        { type: 'checkbox', title: '通所\n(金)', width: 40, readOnly: true },
        { type: 'checkbox', title: '通所\n(土)', width: 40, readOnly: true },
        { type: 'checkbox', title: '通所\n(日)', width: 40, readOnly: true },
        { type: 'text', title: '保険者番号', width: 100, readOnly: true },
        { type: 'text', title: '計画書作成者', width: 100 },
        {
          type: 'dropdown',
          title: '計画書作成者\n(職種)',
          width: 100,
          source: [
            '看護師',
            '准看護師',
            '理学療法士',
            '作業療法士',
            '言語聴覚士',
            '柔道整復師',
            'あん摩マッサージ指圧師',
            'はり師・きゅう師',
            '介護福祉士',
            '医師',
            '歯科医師',
            '薬剤師',
            '保健師',
            '助産師',
            '歯科衛生士',
            '精神保健福祉士',
            '介護支援専門員',
            '社会福祉士',
            '福祉用具専門相談員',
            '管理栄養士',
            '栄養士',
            'その他',
            '不明',
          ],
          autocomplete: true,
        },
        { type: 'text', title: 'プログラム立案者', width: 100 },
        {
          type: 'dropdown',
          title: 'プログラム立案者\n(職種)',
          width: 100,
          source: [
            '看護師',
            '准看護師',
            '理学療法士',
            '作業療法士',
            '言語聴覚士',
            '柔道整復師',
            'あん摩マッサージ指圧師',
            'はり師・きゅう師',
            '介護福祉士',
            '医師',
            '歯科医師',
            '薬剤師',
            '保健師',
            '助産師',
            '歯科衛生士',
            '精神保健福祉士',
            '介護支援専門員',
            '社会福祉士',
            '福祉用具専門相談員',
            '管理栄養士',
            '栄養士',
            'その他',
            '不明',
          ],
          autocomplete: true,
        },
        {
          type: 'dropdown',
          title: '障害高齢者の\n日常生活自立度',
          width: 100,
          source: ['自立', 'J1', 'J2', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'],
        },
        {
          type: 'dropdown',
          title: '認知症高齢者の\n日常生活自立度',
          width: 100,
          source: ['自立', 'Ⅰ', 'Ⅱa', 'Ⅱb', 'Ⅲa', 'Ⅲb', 'Ⅳ', 'M'],
        },
        { type: 'text', title: '利用者本人の希望', width: 100 },
        { type: 'text', title: '家族の希望', width: 100 },
        {
          type: 'dropdown',
          title: '病名',
          width: 100,
          source: disease_name,
          autocomplete: true,
          strict: false,
        },
        {
          type: 'dropdown',
          title: '傷病名コード',
          width: 100,
          source: disease_code,
          autocomplete: true,
          strict: false,
        },
        { type: 'dropdown', title: '発症日・受傷日\n令和or平成', width: 80, source: ['平成', '令和'] },
        { type: 'numeric', title: '発症日・受傷日\n年', width: 80, mask: '00' },
        { type: 'numeric', title: '発症日・受傷日\n月', width: 80, mask: '00' },
        { type: 'numeric', title: '発症日・受傷日\n日', width: 80, mask: '00' },
        { type: 'dropdown', title: '直近の入院日\n令和or平成', width: 80, source: ['平成', '令和'] },
        { type: 'numeric', title: '直近の入院日\n年', width: 80, mask: '00' },
        { type: 'numeric', title: '直近の入院日\n月', width: 80, mask: '00' },
        { type: 'numeric', title: '直近の入院日\n日', width: 80, mask: '00' },
        { type: 'dropdown', title: '直近の退院日\n令和or平成', width: 80, source: ['平成', '令和'] },
        { type: 'numeric', title: '直近の退院日\n年', width: 80, mask: '00' },
        { type: 'numeric', title: '直近の退院日\n月', width: 80, mask: '00' },
        { type: 'numeric', title: '直近の退院日\n日', width: 80, mask: '00' },
        { type: 'text', title: '治療経過\n（手術がある場合は手術日・術式等）', width: 100 },
        // 合併症 23 項目: チェックボックスで「あり/なし」を即時切替。
        // セル値は boolean (true=あり / false=なし)。saveAllData / 初期値ロード側も boolean を扱う。
        ...COMPLICATION_FIELDS.map(({ label }) => ({
          type: 'checkbox',
          title: label,
          width: 70,
        })),
        // { type: 'dropdown', title: '保険者番号', width: 100 },
      ],
      // #857: jSuites の dropdown autocomplete (find) は user 入力をそのまま
      // new RegExp(str, 'gi') に渡すため、'*' '+' '?' '(' '[' 等の正規表現
      // メタ文字が混じると SyntaxError を投げる。さらに setTimeout の中で投げるため
      // keyTimer が永久に null に戻らず、以降の絞り込みも全部ロックされる
      // (delete してから他の文字を入力しても候補が出ない症状の原因)。
      //
      // jspreadsheet の oncreateeditor は createEditor 内で editor を返す直前に
      // 発火するが、jSuites.dropdown(editor, options) はその後に呼ばれて
      // editor.dropdown を設定する。よって oncreateeditor 同期実行内では
      // editor.dropdown はまだ undefined。setTimeout(0) で次の event loop に
      // ずらして jSuites の初期化完了後に find を escape 版に差し替える。
      // (debounce 400ms の前に確実に patch が当たる順序)
      oncreateeditor: (_el: any, _cell: any, _x: any, _y: any, editor: any) => {
        setTimeout(() => {
          const dd = editor && (editor as any).dropdown;
          if (dd && typeof dd.find === 'function' && !dd.__moffFindPatched) {
            const originalFind = dd.find.bind(dd);
            const escapeRegex = (s: string) => String(s ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            dd.find = (str: string) => originalFind(escapeRegex(str));
            dd.__moffFindPatched = true;
          }
        }, 0);
      },
      onchange: (el: any, cell: any, x: number, y: number, value: any, oldValue: any) => {
        // console.log({ el, cell, x, y, value, oldValue });
        // 病名リストのICD10から自動的に選択されるようにする
        x = Number(x);
        // 病名
        if (x === INDEX_OF_ICD10_TRIGGER_1) {
          // (y)行目の(x)列がICD10(病名) : x+2としているのは、xが配列番号で+1して、その右が目的列なので+1
          const indexX = Number(x) + Number(2);
          const indexY = Number(y) + Number(1);
          const cell = to_string_col_index(indexX) + indexY.toString();
          const setValue = (cell: string, value: string) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            jRef.current.jspreadsheet.setValue(cell, value);
          };
          const setDisease = (value: any, cell: string) => {
            // valueが diseaseのnameに該当するものがあればその配列番号のicd10がICD10コードなので、それをセルに挿入
            const code = disease.find((item) => {
              return item.name === value.toString();
            });
            if (code) {
              // #857: code.icd10 は型宣言上 string だが実 API は number で返してくるため、
              //       傷病名コード列 (autocomplete) の filter が壊れないよう明示的に string 化する
              setValue(cell, String(code.icd10));
            }
          };
          setDisease(value, cell);
        }
        // モフらく計画書以外の利用者データのセル (col 1-17) は readOnly:true で
        // 物理的に編集不可。万一 paste 等で onchange が発火した場合の防御網として警告表示。
        if (x <= INDEX_OF_BOUNDARY_FOR_EDITABLE) {
          alert('この情報の変更は反映できません。\nメニューに戻り、利用者設定で変更してください。');
        }
        if (x > INDEX_OF_BOUNDARY_FOR_EDITABLE) {
          //TODO 変更があるたびに一時保存する
          // #834 F: dirty 追跡。値が実際に変わった場合のみマーク (no-op 編集を除外)。
          if (String(value) !== String(oldValue ?? '')) {
            const key = `${y},${x}`;
            dirtyCells.current.add(key);
            // セル td に .dirty クラスを付与してハイライト。
            // (cell 引数は jspreadsheet 内部の td 参照だが互換性確保のため getCellFromCoords も併用)
            try {
              const td =
                (cell as HTMLElement | undefined) ??
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                jRef.current?.jspreadsheet?.getCellFromCoords(x, y);
              if (td && (td as HTMLElement).classList) {
                (td as HTMLElement).classList.add('dirty');
              }
            } catch {
              // DOM 操作の失敗はハイライトのみに影響、保存処理には影響しないため無視
            }
            // #834 C: 自動保存 ON のときは debounce 後にトリガー
            if (autosaveEnabledRef.current) {
              if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
              }
              autosaveTimerRef.current = setTimeout(() => {
                autosaveTimerRef.current = null;
                triggerAutosave();
              }, AUTOSAVE_DEBOUNCE_MS);
            }
          }
        }
      },
    });
  }, [users_table_data, disease]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    if (!jRef.current.jspreadsheet && options.data && options.data.length > 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      jspreadsheet(jRef.current, options);
      // jspreadsheet が search:true で生成する .jexcel_filter の
      // 左側 div (空の paginationContainer) → 自動保存トグル
      // 右側 div (絞り込み検索ボックス) → ヘルプアイコン (#834 説明文整理)
      // を React Portal で注入する
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      const filterChildren = (jRef.current as HTMLElement)?.querySelectorAll?.('.jexcel_filter > div');
      if (filterChildren && filterChildren[0] instanceof HTMLElement) {
        setFilterToolbarEl(filterChildren[0]);
      }
      if (filterChildren && filterChildren[1] instanceof HTMLElement) {
        setSearchToolbarEl(filterChildren[1]);
      }
    }
  }, [options, users_table_data]);

  return (
    <>
      {typeof users === 'undefined' ? (
        <tr>
          <th scope="row">再読み込みしてください。</th>
          <td />
          <td />
          <td />
          <td />
        </tr>
      ) : null}
      <div className={classes.spreadsheet}>
        <p>
          氏名～保険者番号（グレー背景の列）は、この画面では編集できません。変更する場合は利用者設定画面から入力をお願いします。
        </p>
        <p>入力欄が選択式（▼印）の箇所は、ダブルクリックして選択肢から選んでください。</p>
        <p style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          合併症（23 項目）はチェックボックスです。クリックで「あり / なし」を切替できます。
          <Tooltip
            title="表を横スクロールすると全 23 項目を確認できます。チェックあり=「あり」、チェックなし=「なし」として保存されます。"
            arrow
            placement="top"
          >
            <HelpOutlineIcon fontSize="small" style={{ verticalAlign: 'middle', color: '#888', cursor: 'help' }} />
          </Tooltip>
        </p>
        <GeneralPrimaryGhostButton onClick={getAllData} innerText={'保存する'} setCss={'width: 100px; height: 40px;'} />
        <br />
        <div ref={jRef} />
        {/* 自動保存トグル (#834 C): jspreadsheet 生成の絞り込み検索行 (.jexcel_filter) 左側に Portal で注入 */}
        {filterToolbarEl &&
          createPortal(
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* ホバー時に挙動ヒントを表示。文章での説明を画面上から減らすための補助 (#834) */}
              <Tooltip title="ON にすると、編集してから 2 秒後に確認なしで自動保存されます" arrow placement="top">
                <FormControlLabel
                  control={
                    <GreenSwitch checked={autosaveEnabled} onChange={(e) => setAutosaveEnabled(e.target.checked)} />
                  }
                  label={`自動保存 ${autosaveEnabled ? 'ON' : 'OFF'}`}
                />
              </Tooltip>
              {autosaveEnabled && autosaveStatus === 'saving' && <span style={{ color: '#666' }}>保存中…</span>}
              {autosaveEnabled && autosaveStatus === 'saved' && <span style={{ color: '#3a8a3a' }}>保存しました</span>}
              {autosaveEnabled && autosaveStatus === 'error' && (
                <span style={{ color: '#c0392b' }}>自動保存に失敗しました（手動で保存してください）</span>
              )}
              {/* (#834 スクロール可視化) 横スクロール用ボタン。
                  macOS overlay scrollbar が薄くて気づかれにくいため、明示的なクリック導線として配置。 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '8px' }}>
                <Tooltip title="表を左へスクロール" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => scrollTableHorizontally(-SCROLL_STEP_PX)}
                    style={{ background: '#f0f0f0', borderRadius: '4px' }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="表を右へスクロール (合併症など右側の項目を表示)" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => scrollTableHorizontally(SCROLL_STEP_PX)}
                    style={{ background: '#f0f0f0', borderRadius: '4px' }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>,
            filterToolbarEl,
          )}
        {/* 絞り込み検索ヘルプアイコン (#834): 検索ボックス横にホバーで使い方説明 */}
        {searchToolbarEl &&
          createPortal(
            <Tooltip
              title="氏名・被保険者番号など、入力した文字を含む利用者の行だけを表示します。空欄ですべて表示。"
              arrow
              placement="top"
            >
              <HelpOutlineIcon
                fontSize="small"
                style={{ marginLeft: '6px', verticalAlign: 'middle', color: '#888', cursor: 'help' }}
              />
            </Tooltip>,
            searchToolbarEl,
          )}
        <br />
        <GeneralPrimaryGhostButton onClick={getAllData} innerText={'保存する'} setCss={'width: 100px; height: 40px;'} />
        {/*<br />*/}
        {/*<input type="button" onClick={getAllData} value="saveMoffRakuBasicInfoData" />*/}
        {/*<br />*/}
        {/*<input type="button" onClick={download} value="Download" />*/}
      </div>
    </>
  );
};

export default UserInputMoffRakuContainer;
